import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import Tour from '../components/Tour';
import NotionExportButton from '../components/NotionExportButton';
import TrelloExportButton from '../components/TrelloExportButton';
import ClickUpExportButton from '../components/ClickUpExportButton';
import {
  Rocket, Sparkle, Target, Users, MoneyWavy, Translate, Lightning, Trophy, BookOpen,
  ChartLine, Crown, Calendar, Stack, Wrench, Copy, Download, Code, Trash, Tag,
  CaretRight, ArrowRight,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'pt', label: 'Português (PT + BR)' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
];

const BUDGETS = [
  { code: 'low', icon: '🥉' },
  { code: 'medium', icon: '🥈' },
  { code: 'high', icon: '🥇' },
];

const FORMAT_COLORS = {
  course: '#A855F7',
  ebook: '#10B981',
  saas: '#2563EB',
  mentorship: '#F59E0B',
  community: '#EC4899',
  hybrid: '#06B6D4',
};

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label || i18n.t('common.copied'));
  } catch {
    toast.error(i18n.t('common.copy_fail'));
  }
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadMarkdown = (bp) => {
  const b = bp.blueprint || {};
  const r = b.recommendation || {};
  const s = b.structure || {};
  const mr = b.market_research || {};
  const p = b.positioning || {};
  const lr = b.launch_roadmap || {};
  const md = `# Product Blueprint · ${bp.niche}\n\n**Audiência**: ${bp.target_audience}\n\n${b.summary || ''}\n\n---\n\n## 🎯 Recomendação\n\n**Formato**: ${r.format_label || r.format}\n\n${r.reasoning || ''}\n\n### Alternativas\n` +
    (r.alternative_formats || []).map((a) => `- **${a.format}** — ${a.when_better}`).join('\n') +
    `\n\n---\n\n## 📚 Estrutura\n\n**Título sugerido**: ${s.title_suggestion || ''}\n**Duração**: ${s.total_duration || ''}\n\n` +
    (s.modules || []).map((m) => `### Módulo ${m.module_number}: ${m.title}\n**Resultado**: ${m.outcome}\n` +
      (m.lessons || []).map((l, i) => `  ${i + 1}. ${l}`).join('\n')).join('\n\n') +
    `\n\n---\n\n## 📊 Market Research\n\n**Mercado**: ${mr.market_size_summary || ''}\n\n**Sinais de procura**:\n` +
    (mr.demand_signals || []).map((d) => `- ${d}`).join('\n') +
    `\n\n**Preço sugerido**: €${mr.suggested_price_eur?.min}-€${mr.suggested_price_eur?.max} (recomendado: €${mr.suggested_price_eur?.recommended})\n${mr.price_reasoning || ''}\n\n**Concorrentes**:\n` +
    (mr.main_competitors || []).map((c) => `- **${c.name}** · €${c.estimated_price_eur} — Forte: ${c.their_strength} · Lacuna: ${c.their_weakness}`).join('\n') +
    `\n\n**Dores da audiência**:\n` + (mr.audience_pain_points || []).map((p) => `- ${p}`).join('\n') +
    `\n\n---\n\n## 💎 Posicionamento\n\n**USP**: ${p.unique_value_prop || ''}\n\n**Elevator pitch**: ${p.elevator_pitch || ''}\n\n**Diferenciação**:\n` +
    (p.differentiation_points || []).map((d) => `- ${d}`).join('\n') +
    `\n\n**Objecções comuns**:\n` + (p.common_objections || []).map((o) => `- **${o.objection}** → ${o.response}`).join('\n') +
    `\n\n---\n\n## ⏱️ Launch Roadmap (${lr.total_weeks || '?'} semanas)\n\n` +
    (lr.weeks || []).map((w) => `### Semana ${w.week}: ${w.focus}\n` + (w.tasks || []).map((t) => `- ${t}`).join('\n')).join('\n\n') +
    `\n\n---\n\n## 🛠️ Stack tecnológico\n\n` + (b.tech_stack_suggestions || []).map((t) => `- **${t.category}**: ${t.recommended} — ${t.why}`).join('\n');
  const blob = new Blob([md], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `product-blueprint-${bp.blueprint_id}.md`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Subcomponents ───────────────────────────────────────────────

const SectionCard = ({ icon: Icon, title, color = '#A855F7', children, actions }) => (
  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
        <h3 className="font-[Outfit] font-bold">{title}</h3>
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const RecommendationView = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const color = FORMAT_COLORS[data.format] || '#A855F7';
  return (
    <div className="space-y-3">
      <div className="p-4 rounded-xl border" style={{ background: `${color}15`, borderColor: `${color}44` }}>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1" style={{ color }}>{t('pg.rec_format')}</div>
        <div className="text-2xl font-[Outfit] font-bold mb-2">{data.format_label}</div>
        <p className="text-sm text-white/80 leading-relaxed">{data.reasoning}</p>
      </div>
      {Array.isArray(data.alternative_formats) && data.alternative_formats.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('pg.alternatives')}</div>
          <div className="space-y-2">
            {data.alternative_formats.map((a, i) => (
              <div key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-sm font-semibold mb-1 capitalize">{a.format}</div>
                <p className="text-xs text-white/60">{a.when_better}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StructureView = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 flex-wrap text-sm">
        <div><span className="text-white/40">{t('pg.struct_title')}</span> <strong className="text-white">{data.title_suggestion}</strong></div>
        <div><span className="text-white/40">{t('pg.struct_duration')}</span> <strong className="text-white">{data.total_duration}</strong></div>
      </div>
      <div className="space-y-2">
        {(data.modules || []).map((m, i) => (
          <details key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg group" open={i === 0}>
            <summary className="cursor-pointer flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded-full bg-[#A855F7]/20 flex items-center justify-center text-[11px] font-bold text-[#A855F7]">{m.module_number}</span>
                  <span className="font-[Outfit] font-bold text-sm">{m.title}</span>
                </div>
                <p className="text-xs text-white/60 ml-8">{m.outcome}</p>
              </div>
              <CaretRight size={14} className="group-open:rotate-90 transition flex-shrink-0 mt-1" />
            </summary>
            <ul className="mt-3 ml-8 space-y-1.5 text-sm text-white/80">
              {(m.lessons || []).map((l, j) => (
                <li key={j} className="flex items-start gap-2">
                  <span className="text-[10px] text-white/30 mt-1">{m.module_number}.{j + 1}</span>
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </div>
  );
};

const MarketResearchView = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const p = data.suggested_price_eur || {};
  return (
    <div className="space-y-4">
      {data.market_size_summary && (
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('pg.market_size')}</div>
          <p className="text-sm text-white/85">{data.market_size_summary}</p>
        </div>
      )}
      {/* Price triangle */}
      <div className="p-4 bg-gradient-to-br from-[#10B981]/15 to-transparent border border-[#10B981]/30 rounded-xl">
        <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-semibold mb-3">{t('pg.price_suggested')}</div>
        <div className="grid grid-cols-3 gap-3 mb-3">
          {[{ k: t('pg.price_min'), v: p.min }, { k: t('pg.price_rec'), v: p.recommended, hl: true }, { k: t('pg.price_max'), v: p.max }].map((x, i) => (
            <div key={i} className={`text-center p-3 rounded-lg ${x.hl ? 'bg-[#10B981]/20 border-2 border-[#10B981]/60' : 'bg-white/5 border border-white/10'}`}>
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">{x.k}</div>
              <div className="text-2xl font-[Outfit] font-bold" style={{ color: x.hl ? '#10B981' : '#FFF' }}>€{x.v ?? '?'}</div>
            </div>
          ))}
        </div>
        {data.price_reasoning && <p className="text-xs text-white/60 leading-relaxed">{data.price_reasoning}</p>}
      </div>
      {/* Demand signals */}
      {Array.isArray(data.demand_signals) && data.demand_signals.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('pg.demand_signals')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.demand_signals.map((d, i) => (
              <div key={i} className="p-2 bg-white/5 border border-white/10 rounded text-xs text-white/80 flex items-start gap-2">
                <ChartLine size={12} weight="bold" className="text-[#10B981] flex-shrink-0 mt-0.5" />
                <span>{d}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Competitors */}
      {Array.isArray(data.main_competitors) && data.main_competitors.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('pg.main_competitors')}</div>
          <div className="space-y-2">
            {data.main_competitors.map((c, i) => (
              <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
                <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                  <span className="font-[Outfit] font-bold text-sm">{c.name}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="px-2 py-0.5 bg-white/10 rounded text-[10px] uppercase tracking-wider">{c.format}</span>
                    <span className="px-2 py-0.5 bg-[#10B981]/15 border border-[#10B981]/30 rounded text-[10px] text-[#10B981] font-semibold">€{c.estimated_price_eur}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div className="text-white/70"><span className="text-[#10B981] font-semibold">{t('pg.strength')}</span> {c.their_strength}</div>
                  <div className="text-white/70"><span className="text-[#F59E0B] font-semibold">{t('pg.gap')}</span> {c.their_weakness}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Pains */}
      {Array.isArray(data.audience_pain_points) && data.audience_pain_points.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('pg.audience_pains')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.audience_pain_points.map((p, i) => (
              <div key={i} className="p-2 bg-[#EF4444]/8 border border-[#EF4444]/25 rounded text-xs text-white/85 flex items-start gap-2">
                <span className="text-[#EF4444] font-bold">!</span>
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PositioningView = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  return (
    <div className="space-y-3">
      {data.unique_value_prop && (
        <div className="p-4 bg-gradient-to-br from-[#A855F7]/15 to-transparent border border-[#A855F7]/30 rounded-xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] font-semibold mb-1">{t('pg.usp')}</div>
          <p className="text-lg font-[Outfit] font-bold leading-snug">{data.unique_value_prop}</p>
        </div>
      )}
      {data.elevator_pitch && (
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('pg.elevator')}</div>
          <p className="text-sm text-white/85 italic leading-relaxed">&ldquo;{data.elevator_pitch}&rdquo;</p>
        </div>
      )}
      {Array.isArray(data.differentiation_points) && data.differentiation_points.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('pg.diff_points')}</div>
          <ul className="space-y-1.5 text-sm text-white/85">
            {data.differentiation_points.map((d, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-[#A855F7]/20 flex items-center justify-center text-[10px] font-bold text-[#A855F7] flex-shrink-0 mt-0.5">{i + 1}</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(data.common_objections) && data.common_objections.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('pg.objections')}</div>
          <div className="space-y-2">
            {data.common_objections.map((o, i) => (
              <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
                <div className="text-sm font-semibold text-[#F59E0B] mb-1">&ldquo;{o.objection}&rdquo;</div>
                <div className="text-xs text-white/70 pl-3 border-l-2 border-[#10B981]/50">{o.response}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const RoadmapView = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div className="text-sm text-white/60">{t('pg.roadmap_pre')} <strong className="text-white">{t('pg.weeks', { n: data.total_weeks || (data.weeks || []).length })}</strong></div>
      <div className="space-y-2">
        {(data.weeks || []).map((w, i) => (
          <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-[#2563EB]/20 flex items-center justify-center font-bold text-sm text-[#60A5FA]">S{w.week}</div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/40">{t('pg.week', { n: w.week })}</div>
                <div className="font-[Outfit] font-bold text-sm">{w.focus}</div>
              </div>
            </div>
            <ul className="space-y-1 text-sm text-white/80 ml-10">
              {(w.tasks || []).map((t, j) => (
                <li key={j} className="flex items-start gap-2">
                  <span className="text-[#10B981] text-xs mt-0.5">▸</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const TechStackView = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {data.map((t, i) => (
        <div key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t.category}</div>
          <div className="font-semibold text-sm text-white mb-1">{t.recommended}</div>
          <p className="text-xs text-white/60">{t.why}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────────

const ProductGenerator = ({ user }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [niche, setNiche] = useState(searchParams.get('niche') || '');
  const [audience, setAudience] = useState(searchParams.get('audience') || '');
  const [sourceUrl, setSourceUrl] = useState(searchParams.get('url') || '');
  const [budget, setBudget] = useState('low');
  const [language, setLanguage] = useState('pt');
  const [loading, setLoading] = useState(false);
  const [blueprint, setBlueprint] = useState(null);
  const [activeTab, setActiveTab] = useState('recommendation');
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/product-generator/history`);
      setHistory(res.data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    const h = () => loadHistory();
    window.addEventListener('brand:changed', h);
    return () => window.removeEventListener('brand:changed', h);
  }, [loadHistory]);

  const generate = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!niche.trim() || !audience.trim()) {
      toast.error(t('pg.err_fill'));
      return;
    }
    setLoading(true);
    setBlueprint(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/product-generator/generate`, {
        niche: niche.trim(),
        target_audience: audience.trim(),
        budget_range: budget,
        language,
        source_url: sourceUrl.trim() || null,
      });
      setBlueprint(res.data);
      setActiveTab('recommendation');
      toast.success(t('pg.gen_success'));
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('pg.gen_fail'));
    } finally {
      setLoading(false);
    }
  }, [niche, audience, sourceUrl, budget, language, loadHistory, t]);

  const openBlueprint = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/product-generator/${id}`);
      setBlueprint(res.data);
      setNiche(res.data.niche);
      setAudience(res.data.target_audience);
      setBudget(res.data.budget_range || 'low');
      setLanguage(res.data.language || 'pt');
      setSourceUrl(res.data.source_url || '');
      setActiveTab('recommendation');
    } catch {
      toast.error(t('pg.open_fail'));
    } finally {
      setLoading(false);
    }
  };

  const deleteBlueprint = async (id) => {
    if (!window.confirm(t('pg.confirm_delete'))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/product-generator/${id}`);
      toast.success(t('pg.deleted'));
      if (blueprint?.blueprint_id === id) setBlueprint(null);
      loadHistory();
    } catch {
      toast.error(t('pg.delete_fail'));
    }
  };

  const tabs = useMemo(() => ([
    { id: 'recommendation', label: t('pg.tab_recommendation'), icon: Target, color: '#A855F7' },
    { id: 'structure', label: t('pg.tab_structure'), icon: BookOpen, color: '#10B981' },
    { id: 'market', label: t('pg.tab_market'), icon: ChartLine, color: '#F59E0B' },
    { id: 'positioning', label: t('pg.tab_positioning'), icon: Crown, color: '#EC4899' },
    { id: 'roadmap', label: t('pg.tab_roadmap'), icon: Calendar, color: '#2563EB' },
    { id: 'tech', label: t('pg.tab_tech'), icon: Stack, color: '#06B6D4' },
  ]), [t]);

  return (
    <DashboardLayout user={user} activePage="/product-generator">
      <Tour tourId="product-generator-v1" steps={[
        { target: '[data-testid="product-gen-niche-input"]', content: t('pg.tour_1'), disableBeacon: true },
        { target: '[data-testid="product-gen-audience-input"]', content: t('pg.tour_2') },
        { target: '[data-testid="product-gen-url-input"]', content: t('pg.tour_3') },
        { target: '[data-testid="product-gen-submit-btn"]', content: t('pg.tour_4') },
      ]} />
      <div className="p-6" data-testid="product-generator-page">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#A855F7]/15 border border-[#A855F7]/30 rounded-full mb-3">
            <Rocket size={14} weight="fill" className="text-[#A855F7]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A855F7] font-semibold">Product Generator · GPT-5.2</span>
          </div>
          <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">Product Generator AI™</h1>
          <p className="text-white/60 max-w-2xl">
            {t('pg.sub_pre')} <strong className="text-white">{t('pg.sub_strong')}</strong>{t('pg.sub_post')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={generate} className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6" data-testid="product-gen-form">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('pg.niche_label')}</label>
              <div className="relative">
                <Target size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text" value={niche} onChange={(e) => setNiche(e.target.value)}
                  placeholder={t('pg.niche_ph')}
                  data-testid="product-gen-niche-input" required
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('pg.audience_label')}</label>
              <div className="relative">
                <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text" value={audience} onChange={(e) => setAudience(e.target.value)}
                  placeholder={t('pg.audience_ph')}
                  data-testid="product-gen-audience-input" required
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition"
                />
              </div>
            </div>
            <div className="lg:col-span-6">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('pg.budget_label')}</label>
              <div className="grid grid-cols-3 gap-2">
                {BUDGETS.map((b) => (
                  <button key={b.code} type="button" onClick={() => setBudget(b.code)}
                    data-testid={`product-gen-budget-${b.code}`}
                    className={`p-2 rounded-lg border text-left transition ${budget === b.code ? 'bg-[#A855F7]/15 border-[#A855F7]/50' : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
                    <div className="text-lg mb-0.5">{b.icon}</div>
                    <div className="text-[10px] text-white/70 leading-tight">{t(`pg.budget_${b.code}`)}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('pg.language')}</label>
              <div className="relative">
                <Translate size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <select value={language} onChange={(e) => setLanguage(e.target.value)}
                  data-testid="product-gen-language-select"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition appearance-none cursor-pointer">
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code} className="bg-[#0A0A0A]">{l.label}</option>)}
                </select>
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('pg.url_label')}</label>
              <input
                type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://..."
                data-testid="product-gen-url-input"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition"
              />
            </div>
            <div className="lg:col-span-12">
              <button type="submit" disabled={loading || !niche.trim() || !audience.trim()}
                data-testid="product-gen-submit-btn"
                className="w-full px-5 py-3 bg-gradient-to-r from-[#A855F7] to-[#7C3AED] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20">
                {loading ? (
                  <><Lightning size={16} weight="fill" className="animate-pulse" /> {t('pg.generating')}</>
                ) : (
                  <><Sparkle size={16} weight="fill" /> {t('pg.generate_btn')}</>
                )}
              </button>
            </div>
          </div>
        </form>

        {loading && !blueprint && (
          <div className="p-10 bg-[#0A0A0A] border border-white/10 rounded-2xl text-center" data-testid="product-gen-loading">
            <Rocket size={32} weight="fill" className="mx-auto mb-4 text-[#A855F7] animate-pulse" />
            <p className="text-white/70 mb-1">{t('pg.loading_pre')} <strong className="text-white">{t('pg.loading_strong')}</strong>...</p>
            <p className="text-white/40 text-sm">{t('pg.loading_sub')}</p>
          </div>
        )}

        {blueprint && !loading && (
          <div className="space-y-4">
            <div className="p-5 bg-gradient-to-br from-[#A855F7]/10 to-transparent border border-[#A855F7]/30 rounded-2xl" data-testid="product-gen-result-header">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] font-semibold mb-1">{t('pg.bp_generated')}</div>
                  <h2 className="text-2xl font-[Outfit] font-bold mb-1">{blueprint.niche}</h2>
                  <p className="text-sm text-white/60 mb-2">{blueprint.target_audience}</p>
                  {blueprint.blueprint?.summary && (
                    <p className="text-sm text-white/80 leading-relaxed border-l-2 border-[#A855F7]/40 pl-3 mt-3">{blueprint.blueprint.summary}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => downloadMarkdown(blueprint)} data-testid="product-gen-export-md"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition">
                    <Download size={13} weight="bold" /> Markdown
                  </button>
                  <button onClick={() => downloadJson(blueprint, `product-blueprint-${blueprint.blueprint_id}.json`)} data-testid="product-gen-export-json"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition">
                    <Code size={13} weight="bold" /> JSON
                  </button>
                  <NotionExportButton
                    shape_type="product_blueprint"
                    source_id={blueprint.blueprint_id}
                    testId="product-gen-export-notion"
                    variant="ghost"
                  />
                  <TrelloExportButton
                    shape_type="product_blueprint"
                    source_id={blueprint.blueprint_id}
                    testId="product-gen-export-trello"
                    variant="ghost"
                  />
                  <ClickUpExportButton
                    shape_type="product_blueprint"
                    source_id={blueprint.blueprint_id}
                    testId="product-gen-export-clickup"
                    variant="ghost"
                  />
                  <button onClick={() => navigate(`/auto-build?niche=${encodeURIComponent(blueprint.niche)}&offer=${encodeURIComponent(blueprint.blueprint?.structure?.title_suggestion || blueprint.target_audience)}`)}
                    data-testid="product-gen-to-autobuild-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-lg text-xs uppercase tracking-wider font-semibold transition">
                    <Wrench size={13} weight="bold" /> Gerar funil
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 p-1 bg-[#0A0A0A] border border-white/10 rounded-xl flex-wrap" data-testid="product-gen-tabs">
              {tabs.map((t) => {
                const Icon = t.icon; const active = activeTab === t.id;
                return (
                  <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`product-gen-tab-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${active ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80'}`}
                    style={active ? { boxShadow: `inset 0 0 0 1px ${t.color}44` } : {}}>
                    <Icon size={14} weight={active ? 'fill' : 'bold'} style={{ color: active ? t.color : undefined }} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'recommendation' && (
              <SectionCard icon={Target} color="#A855F7" title={t('pg.sec_recommendation')}>
                <RecommendationView data={blueprint.blueprint?.recommendation} />
              </SectionCard>
            )}
            {activeTab === 'structure' && (
              <SectionCard icon={BookOpen} color="#10B981" title={t('pg.sec_structure')}>
                <StructureView data={blueprint.blueprint?.structure} />
              </SectionCard>
            )}
            {activeTab === 'market' && (
              <SectionCard icon={ChartLine} color="#F59E0B" title={t('pg.sec_market')}>
                <MarketResearchView data={blueprint.blueprint?.market_research} />
              </SectionCard>
            )}
            {activeTab === 'positioning' && (
              <SectionCard icon={Crown} color="#EC4899" title={t('pg.sec_positioning')}>
                <PositioningView data={blueprint.blueprint?.positioning} />
              </SectionCard>
            )}
            {activeTab === 'roadmap' && (
              <SectionCard icon={Calendar} color="#2563EB" title={t('pg.sec_roadmap')}>
                <RoadmapView data={blueprint.blueprint?.launch_roadmap} />
              </SectionCard>
            )}
            {activeTab === 'tech' && (
              <SectionCard icon={Stack} color="#06B6D4" title={t('pg.sec_tech')}>
                <TechStackView data={blueprint.blueprint?.tech_stack_suggestions} />
              </SectionCard>
            )}
          </div>
        )}

        {!blueprint && !loading && history.length > 0 && (
          <div className="mt-8" data-testid="product-gen-history">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40 mb-3">{t('pg.history_title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((h) => (
                <div key={h.blueprint_id} className="p-4 bg-[#121212] border border-white/10 rounded-xl hover:border-white/20 transition group" data-testid={`product-gen-history-${h.blueprint_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Tag size={13} weight="bold" className="text-[#A855F7] flex-shrink-0" />
                      <span className="text-xs text-white/40 uppercase tracking-wider truncate">{h.blueprint?.recommendation?.format_label || h.language?.toUpperCase()}</span>
                    </div>
                    <button onClick={() => deleteBlueprint(h.blueprint_id)} data-testid={`product-gen-history-delete-${h.blueprint_id}`}
                      className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition" title={t('pg.delete_tip')}>
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                  <button onClick={() => openBlueprint(h.blueprint_id)} data-testid={`product-gen-history-open-${h.blueprint_id}`} className="text-left w-full">
                    <div className="font-[Outfit] font-bold text-white truncate mb-1">{h.niche}</div>
                    <div className="text-xs text-white/50 line-clamp-2 leading-snug mb-2">{h.target_audience}</div>
                    <div className="text-[10px] text-white/30 uppercase tracking-wider">{new Date(h.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!blueprint && !loading && history.length === 0 && (
          <div className="p-8 bg-[#121212] border border-white/10 rounded-2xl text-center" data-testid="product-gen-empty">
            <Rocket size={32} weight="bold" className="mx-auto mb-3 text-white/30" />
            <h3 className="font-[Outfit] font-bold text-lg mb-1">{t('pg.empty_title')}</h3>
            <p className="text-white/60 text-sm">{t('pg.empty_sub')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProductGenerator;
